import React, { useState, useEffect } from "react";
import apiService from "../services/apiService";
import {
  Container,
  Chip,
  Tooltip,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormHelperText,
  CssBaseline,
  Box,
  Autocomplete,
  CircularProgress,
  Menu,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ThemeProvider } from "@mui/material/styles";
import FailureTrackingPopup from "../Components/FailureTrackingPopup";
import { ReactComponent as StartTimeIcon } from "../Icons/Start time.svg";
import { ReactComponent as EndTimeIcon } from "../Icons/End time.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";
import { ReactComponent as EditIcon } from "../Icons/Edit.svg";
import { ReactComponent as NotesIcon } from "../Icons/Notes.svg";
import { ReactComponent as FailureReasonIcon } from "../Icons/Failure reason log detail.svg";
import { ReactComponent as CloneIcon } from "../Icons/Clone.svg";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIconSVG } from "../Icons/Settings.svg";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import Snackbar from "@mui/material/Snackbar";
import dayjs from "dayjs";
import CommonPagination from "../Components/Pagination";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  formatDateTimezoneFreeForFailureTracking,
  formatFailureTrackingDateTime,
  toFailureTrackingInputFormat,
  cleanFailureTrackingDate,
} from "../utils/failureTrackingDateUtils";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getCurrentDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(today.getDate()).padStart(2, "0")}`;
};

const today = getCurrentDate();

const largeMultilineStyles = {
  "& .MuiInputBase-root": {
    fontSize: "0.85rem",
    padding: "0 10px",
    width: "100%",
    display: "flex",
    alignItems: "flex-start",
  },
  "& .MuiInputBase-input": {
    padding: "8px 10px",
    lineHeight: "1.2",
    textAlign: "left",
    overflowY: "auto",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    top: "-2px",
  },
  "& .MuiInputLabel-shrink": {
    top: "0",
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.75rem",
  },
};
const compactMultilineStyles = {
  "& .MuiInputBase-root": {
    minHeight: "80px",
    fontSize: "0.85rem",
    padding: "0 10px",
  },
  "& .MuiInputBase-input": {
    padding: "8px 10px",
    lineHeight: "1.2",
    overflowY: "auto",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    top: "-2px",
  },
  "& .MuiInputLabel-shrink": {
    top: "0",
  },
  "& .MuiFormHelperText-root": {
    fontSize: "0.75rem",
  },
};

function FailureTracking() {
  const [failures, setFailures] = useState([]);
  const [clients, setClients] = useState([]);
  const [exportFilters, setExportFilters] = useState({
    reportedDateFrom: today,
    reportedDateTo: today,
    receivedDateFrom: "",
    receivedDateTo: "",
    env_id: "",
    app_id: "",
  });
  const [selectedJobScript, setSelectedJobScript] = useState("");
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);

  // Cascading filter states
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);

  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedJobDetails, setSelectedJobDetails] = useState([]);
  const [selectedJeId, setSelectedJeId] = useState("");
  const [editField, setEditField] = useState(null);
  const [editError, setEditError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [loadingFailures, setLoadingFailures] = useState(true);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [uniqueFolders, setUniqueFolders] = useState([]);
  const [fromReceivedDate, setFromReceivedDate] = useState("");
  const [toReceivedDate, setToReceivedDate] = useState("");
  const [receivedDateError, setReceivedDateError] = useState("");
  const [openExportDialog, setOpenExportDialog] = useState(false);
  const [jobsListForFilters, setJobsListForFilters] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] = useState(null);
  const [confirmClone, setConfirmClone] = useState({
    open: false,
    je_id: null,
  });

  const handleExportCSV = async () => {
    // 1. Validation: at least one date range must be selected
    const hasReportedDates =
      exportFilters.reportedDateFrom && exportFilters.reportedDateTo;
    const hasReceivedDates =
      exportFilters.receivedDateFrom && exportFilters.receivedDateTo;

    if (!hasReportedDates && !hasReceivedDates) {
      setErrorMessage(
        "Please select at least one date range (either Reported Date or Received Date)",
      );
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }

    try {
      // 2. Build query parameters
      const params = new URLSearchParams();

      if (hasReportedDates) {
        params.append("start_date", exportFilters.reportedDateFrom);
        params.append("end_date", exportFilters.reportedDateTo);
      }

      if (hasReceivedDates) {
        params.append("received_date_from", exportFilters.receivedDateFrom);
        params.append("received_date_to", exportFilters.receivedDateTo);
      }

      if (exportFilters.app_id) {
        params.append("appId", exportFilters.app_id);
      }

      if (exportFilters.env_id) {
        params.append("env_id", exportFilters.env_id);
      }

      const endpoint = `/job_errors/job_error_details/export/csv?${params.toString()}`;

      // 3. Use raw fetch with apiService headers (same pattern as Job Monitoring)
      const response = await fetch(`${BASE_URL}${endpoint}`, {
        method: "GET",
        headers: {
          ...apiService.getAuthHeaders(), // ← Automatically adds Authorization: Bearer token
          Accept: "text/csv", // Important for correct response type
        },
      });

      // 4. Handle error responses
      if (!response.ok) {
        let errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.msg || errorJson.error || errorText;
        } catch {
          // Keep plain text if not JSON
        }
        throw new Error(`Export failed (${response.status}): ${errorText}`);
      }

      // 5. Get CSV as blob
      const blob = await response.blob();

      // 6. Create dynamic filename
      let filename = "job_error_details";

      if (hasReportedDates) {
        filename += `_reported_${exportFilters.reportedDateFrom}_to_${exportFilters.reportedDateTo}`;
      }
      if (hasReceivedDates) {
        filename += `_received_${exportFilters.receivedDateFrom}_to_${exportFilters.receivedDateTo}`;
      }
      if (exportFilters.app_id) {
        filename += `_app_${exportFilters.app_id}`;
      }
      if (exportFilters.env_id) {
        filename += `_env_${exportFilters.env_id}`;
      }
      filename += ".csv";

      // 7. Trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // 8. Success feedback
      setOpenExportDialog(false);
      setSuccessMessage("Export completed successfully!");
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (error) {
      console.error("Export error:", error);
      setErrorMessage(`Export failed: ${error.message}`);
      setTimeout(() => setErrorMessage(""), 7000);
    }
  };

  const handleFromReceivedDateChange = (value) => {
    setFromReceivedDate(value);
    setToReceivedDate(value);
    setFilter((prev) => ({
      ...prev,
      fromReceivedDate: value,
      toReceivedDate: value,
    }));
    // Validation: From can't be after To
    if (toReceivedDate && new Date(value) > new Date(toReceivedDate)) {
      setReceivedDateError("From date cannot be after To date");
    } else {
      setReceivedDateError("");
    }
  };
  const toDateObj = (value) => {
    if (!value) return null;
    // Strict validation for YYYY-MM-DD format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const d = new Date(value + "T00:00:00");
    return isNaN(d.getTime()) ? null : d;
  };
  const toISO = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-CA"); // YYYY-MM-DD
  };
  const formatNiceDate = (value) => {
    if (!value) return "";
    // value like "2025-11-29" or "2025-11-29T00:00:00.000Z"
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
  };

  const handleToReceivedDateChange = (value) => {
    setToReceivedDate(value);
    setFilter((prev) => ({ ...prev, toReceivedDate: value }));
    // Validation: To can't be before From
    if (fromReceivedDate && new Date(value) < new Date(fromReceivedDate)) {
      setReceivedDateError("To date cannot be before From date");
    } else {
      setReceivedDateError("");
    }
  };
  useEffect(() => {
    apiService
      .get("/job_errors/folders-list")
      .then((data) => {
        setUniqueFolders(data || []);
      })
      .catch((err) => {
        console.error("Error fetching folders:", err);
        setUniqueFolders([]);
      });
  }, []);
  const [formData, setFormData] = useState({
    job_id: "",
    cl_id: clients.length > 0 ? clients[0].cl_id : "",
    client_name: "",
    env_id: "",
    env_name: "",
    app_id: "",
    app_name: "",
    client_job_id: "",
    je_id: "",
    received_date: new Date().toISOString().split("T")[0],
    reference_date_1: new Date().toISOString().split("T")[0],
    start_time: "",
    end_time: "",
    job_status: "",
    failure_message: "",
    comments: "",
    scheduled_time: "",
    last_run_time: "",
    success_cnt: "",
    failures_cnt: "",
    return_count: "",
    doc_id: "",
    run_time: "",
    err_identified_by: "",
    jobs_script: "",
    frequency: "",
    step_taken: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});

  // Check if form has changed
  const hasFormChanged = () => {
    if (!isEditing) return true;
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [alert, setAlert] = useState({ open: false, message: "" });
  const [dateError, setDateError] = useState("");
  const [editData, setEditData] = useState(null);

  // Update client when clients are loaded and dialog is open for adding
  useEffect(() => {
    if (!isEditing && openDialog && clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
      }));
    }
  }, [clients, isEditing, openDialog, formData.cl_id]);

  const handleFromDateChange = (value) => {
    setFromReportedDate(value);
    setToReportedDate(value);
    // Always update To Date if it's before the new From Date
    setToReportedDate(value);
    if (!toReportedDate || new Date(value) > new Date(toReportedDate)) {
    }

    // Validation
    if (toReportedDate && new Date(value) > new Date(toReportedDate)) {
      setDateError("From date cannot be after To date");
    } else {
      setDateError("");
    }
  };

  const handleToDateChange = (value) => {
    setToReportedDate(value);

    // Always update From Date if it's after the new To Date
    if (!fromReportedDate || new Date(value) < new Date(fromReportedDate)) {
      setFromReportedDate(value);
    }

    // Validation
    if (fromReportedDate && new Date(value) < new Date(fromReportedDate)) {
      setDateError("To date cannot be before From date");
    } else {
      setDateError("");
    }
  };

  const [editSuccess, setEditSuccess] = useState("");
  const [filter, setFilter] = useState({
    env_id: "",
    app_id: "",
    job_id: "",
    jobs_script: "",
    active_flag: "Active",
    reference_date_1: today,
    fromReportedDate: today,
    toReportedDate: today,
    fromReceivedDate: "",
    toReceivedDate: "",
    scope: "",
    process_type: "",
    folder: "",
  });
  const [fromReportedDate, setFromReportedDate] = useState(today);
  const [toReportedDate, setToReportedDate] = useState(today);
  const [showFilters, setShowFilters] = useState(true);

  const handleResetFilter = () => {
    const today = new Date().toISOString().split("T")[0];

    setFilter({
      env_id: "",
      app_id: "",
      job_id: "",
      jobs_script: "",
      reference_date_1: today,
      scope: "",
      process_type: "",
      folder: "",
      status: "active",
    });

    setFromReportedDate(today);
    setToReportedDate(today);

    setCurrentPage(1);

    fetchFailures();
  };

  const defaultColumns = {
    client_name: true,
    env_name: true,
    app_id: true,
    je_id: true,
    job_id: true,
    client_job_id: true,
    jobs_script: true,
    frequency: true,
    // scope: true,
    process_type: true,
    reference_date_1: true,
    received_date: true,
    // scheduled_time: true,
    // last_run_time: true,
    // success_cnt: true,
    failures_cnt: true,
    // return_count: true,
    // // doc_id: true,
    failure_message: true,
    // start_time: false,
    // end_time: false,
    // run_time: true,
    job_status: true,
    // err_identified_by: true,
    comments: true,
    active_flag: true,
    folder: true,
  };

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem("failureTrackingVisibleColumns");
    return saved ? JSON.parse(saved) : defaultColumns;
  });
  useEffect(() => {
    localStorage.setItem(
      "failureTrackingVisibleColumns",
      JSON.stringify(visibleColumns),
    );
  }, [visibleColumns]);
  const columnLabels = {
    client_name: "Client",
    env_name: "Environment",
    app_id: "Application",
    je_id: "Tracking ID",
    job_id: "Job ID",
    client_job_id: "Client Job ID",
    jobs_script: "Job Name",
    frequency: "Frequency",
    // scope: "Scope",
    process_type: "Process Type",
    reference_date_1: "Reported Date",
    received_date: "Received Date",
    // scheduled_time: "Scheduled Time",
    // last_run_time: "Last Run Time",
    // success_cnt: "Success Count",
    failures_cnt: "Failures Count",
    // return_count: "Return Count",
    // doc_id: "Document ID",
    folder:"Folder",
    failure_message: "Failure Message",
    // start_time: "Start Time",
    // end_time: "End Time",
    // run_time: "Run Time",
    job_status: "Job Status",
    // err_identified_by: "Identified By",
    comments: "Comments",
    active_flag: "Active Flag",
  };

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  useEffect(() => {
    fetchFailures();
    fetchDropdowns();
  }, [
    currentPage,
    rowsPerPage,
    filter,
    fromReportedDate,
    toReportedDate,
    fromReceivedDate,
    toReceivedDate,
  ]);

  // Fetch jobs list for filters when env_id and app_id are selected
  useEffect(() => {
    const fetchJobsForFilters = async () => {
      if (filter.env_id && filter.app_id) {
        try {
          const jobsData = await apiService.get(
            `/joberrors/jobs/by-context?env_id=${filter.env_id}&app_id=${filter.app_id}`,
          );
          setJobsListForFilters(jobsData || []);
        } catch (error) {
          console.error("Error fetching jobs for filters:", error);
          setJobsListForFilters([]);
        }
      } else {
        setJobsListForFilters([]);
      }
    };
    fetchJobsForFilters();
  }, [filter.env_id, filter.app_id]);

  const fetchFailures = async () => {
    setLoadingFailures(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: rowsPerPage.toString(),
        ...(filter.env_id && { env_id: filter.env_id }),
        ...(filter.app_id && { app_id: filter.app_id }),
        ...(filter.job_id && { job_id: filter.job_id }),
        ...(filter.jobs_script && { jobs_script: filter.jobs_script }),
        ...(fromReportedDate && { reference_date_from: fromReportedDate }),
        ...(toReportedDate && { reference_date_to: toReportedDate }),
        ...(fromReceivedDate && { received_date_from: fromReceivedDate }),
        ...(toReceivedDate && { received_date_to: toReceivedDate }),
        ...(filter.scope && { scope: filter.scope }),
        ...(filter.process_type && { process_type: filter.process_type }),
        ...(filter.folder && { folder: filter.folder }),
        ...(filter.status === "active" && { active_flag: 1 }),
        ...(filter.status === "inactive" && { active_flag: 0 }),
      }).toString();

      // Debug log
      const response = await apiService.request(`/job_errors?${queryParams}`, {
        method: "GET",
      });
      const { data, pagination } = response;

      setFailures(data || []);
      setTotalPages(pagination.totalPages || 1);
      setTotalRecords(pagination.totalRecords || 0);
    } catch (err) {
      setFailures([]);
      setErrorMessage("Failed to fetch failures.");
      setTimeout(() => setErrorMessage(""), 1000);
      console.error("Fetch error:", err.message);
    } finally {
      setLoadingFailures(false);
    }
  };

  const fetchDropdowns = async () => {
    setLoadingDropdowns(true);
    try {
      const [clientsRes, envsRes, appsRes, jobsRes] = await Promise.all([
        apiService.get("/clients").catch((err) => {
          console.error("Error fetching clients:", err);
          throw new Error("Failed to fetch clients");
        }),
        apiService.get("/environments").catch((err) => {
          console.error("Error fetching environments:", err);
          throw new Error("Failed to fetch environments");
        }),
        apiService.get("/applications").catch((err) => {
          console.error("Error fetching applications:", err);
          throw new Error("Failed to fetch applications");
        }),
        apiService.get("/jobs").catch((err) => {
          console.error("Error fetching jobs:", err);
          throw new Error("Failed to fetch jobs");
        }),
      ]);
      setClients(clientsRes);
      setEnvironments(envsRes);
      setApplications(appsRes);
      setJobs(jobsRes);
      // Also set jobsListForFilters with all jobs initially
      setJobsListForFilters(jobsRes);
    } catch (error) {
      console.error("Error fetching dropdown data:", error.message);
      setErrorMessage(
        error.message ||
          "Failed to load dropdown data. Please check the backend or try again.",
      );
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setLoadingDropdowns(false);
    }
  };

  // Use applications from backend, not from failures list
  const uniqueApps = applications.map((app) => app.app_id);

  // Use environments from backend, not from failures list
  const uniqueEnvs = environments.map((env) => env.env_id);

  const uniqueJobIds = [
    ...new Set(failures.map((detail) => detail.job_id)),
  ].map((jobId) => {
    const detail = failures.find((d) => d.job_id === jobId);
    return {
      job_id: jobId,
      jobs_script: detail?.jobs_script || "N/A",
    };
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === "env_id") {
      setFilter((prev) => ({
        ...prev,
        env_id: value,
        app_id: "", // Clear dependent filters
        job_id: "",
        jobs_script: "",
      }));
    } else if (name === "app_id") {
      setFilter((prev) => ({
        ...prev,
        app_id: value,
        job_id: "", // Clear dependent filters
        jobs_script: "",
      }));
    } else {
      setFilter((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Cascading filter: Fetch applications when environment changes
  useEffect(() => {
    const fetchFilteredApplications = async () => {
      if (!filter.env_id) {
        setFilteredApplications(applications);
        return;
      }

      try {
        const data = await apiService.get(
          `/job_errors/applications/by-environment?env_id=${filter.env_id}`,
        );
        setFilteredApplications(data || []);
      } catch (error) {
        console.error("Error fetching filtered applications:", error);
        setFilteredApplications([]);
      }
    };

    fetchFilteredApplications();
  }, [filter.env_id, applications]);

  // Cascading filter: Fetch jobs when environment or application changes
  useEffect(() => {
    const fetchFilteredJobs = async () => {
      if (!filter.env_id || !filter.app_id) {
        setFilteredJobs(jobs);
        return;
      }

      try {
        const data = await apiService.get(
          `/job_errors/jobs/by-context?env_id=${filter.env_id}&app_id=${filter.app_id}`,
        );
        setFilteredJobs(data || []);
      } catch (error) {
        console.error("Error fetching filtered jobs:", error);
        setFilteredJobs([]);
      }
    };

    fetchFilteredJobs();
  }, [filter.env_id, filter.app_id, jobs]);

  useEffect(() => {
    fetchFailures();
  }, [currentPage, rowsPerPage, filter]);

  const filteredFailures = failures
    .map((failure) => {
      const job = jobs.find((j) => j.job_id === failure.job_id);
      return {
        ...failure,
        jobs_script: job?.jobs_script || "",
        frequency: job?.frequency || "",
        scope: job?.scope || "",
        process_type: job?.process_type || "",
      };
    })
    .filter(
      (failure) =>
        (!filter.env_id || failure.env_id === filter.env_id) &&
        (!filter.app_id || failure.app_id === filter.app_id) &&
        (!filter.job_id || failure.job_id === filter.job_id) &&
        (!filter.reference_date_1 ||
          formatFailureTrackingDateTime(failure.reference_date_1, "date") ===
            filter.reference_date_1) &&
        (!filter.scope || failure.scope === filter.scope) &&
        (!filter.process_type || failure.process_type === filter.process_type),
    );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedData = { ...prev, [name]: value };

      // Cascading validation logic
      if (name === "env_id") {
        updatedData.app_id = "";
        updatedData.job_id = "";
        updatedData.client_job_id = "";
      } else if (name === "app_id") {
        updatedData.job_id = "";
        updatedData.client_job_id = "";
      }

      return updatedData;
    });
  };

  const handleAutocompleteChange = (
    name,
    value,
    relatedField = null,
    relatedValue = null,
  ) => {
    if (name === "job_id") {
      if (value) {
        const selectedJob = jobs.find((job) => job.job_id === value);
        if (selectedJob) {
          const selectedApp = applications.find(
            (app) => app.app_id === selectedJob.app_id,
          );
          const isBoomi = selectedApp?.name === "Boomi";
          setFormData({
            ...formData,
            job_id: value,
            client_job_id: selectedJob.client_job_id || "",
            ...(isBoomi
              ? {
                  scheduled_time: "",
                  last_run_time: "",
                  success_cnt: "",
                  return_count: "",
                  doc_id: "",
                  run_time: "",
                  err_identified_by: "",
                  step_taken: "",
                }
              : {}),
          });
        }
      } else {
        setFormData({
          ...formData,
          job_id: "",
          client_job_id: "",
          scheduled_time: "",
          last_run_time: "",
          success_cnt: "",
          return_count: "",
          doc_id: "",
          run_time: "",
          err_identified_by: "",
          step_taken: "",
        });
      }
    } else if (relatedField && relatedValue) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        [relatedField]: relatedValue,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    const errors = {};
    if (!formData.received_date)
      errors.received_date = "Received date is required.";
    if (!formData.reference_date_1)
      errors.reference_date_1 = "Reported date is required";
    if (
      !formData.job_status ||
      typeof formData.job_status !== "string" ||
      !formData.job_status.trim()
    ) {
      errors.job_status = "Status is required.";
    }
    if (!formData.cl_id) errors.cl_id = "Client is required.";
    if (!formData.env_id) errors.env_id = "Environment is required.";
    if (!formData.app_id) errors.app_id = "Application is required.";
    if (!formData.job_id) errors.job_id = "Job ID is required.";
    if (!formData.je_id) errors.je_id = "Failure Tracking ID is required.";

    if (formData.success_cnt && isNaN(formData.success_cnt))
      errors.success_cnt = "Success count must be a number.";
    if (formData.failures_cnt && isNaN(formData.failures_cnt))
      errors.failures_cnt = "Failures count must be a number.";
    if (formData.return_count && isNaN(formData.return_count))
      errors.return_count = "Return count must be a number.";
    return errors;
  };

  function toDbFormat(inputValue, isTimeOnly = false) {
    if (!inputValue || typeof inputValue !== "string") return null;

    try {
      if (isTimeOnly) {
        const timePart = inputValue.includes("T")
          ? inputValue.split("T")[1]
          : inputValue;
        const [hours, minutes] = timePart.split(":");
        if (!hours || !minutes) return null;
        return `${timePart}:00.000`;
      }

      const cleanedInput = inputValue.replace("T", " ");
      const [datePart, timePart] = cleanedInput.split(" ");
      if (
        !datePart ||
        !timePart ||
        !datePart.match(/^\d{4}-\d{2}-\d{2}$/) ||
        !timePart.match(/^\d{2}:\d{2}$/)
      ) {
        return null;
      }
      return `${cleanedInput}:00.000`;
    } catch (error) {
      console.error("Error formatting date/time:", error);
      return null;
    }
  }

  // Use the new timezone-free date formatting utility
  // This ensures consistent date display regardless of user's timezone

  function toInputFormat(dbValue) {
    if (!dbValue) return "";
    try {
      const [datePart, timePartRaw] = dbValue.split(" ");
      const [hours, minutes] = timePartRaw.split(":");
      return `${datePart}T${hours}:${minutes}`;
    } catch (error) {
      console.error("Invalid dbValue in toInputFormat:", dbValue);
      return "";
    }

    try {
      const date = new Date(dbValue);
      if (isNaN(date.getTime())) {
        console.error("Invalid date in toInputFormat:", dbValue);
        return "";
      }
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      const formatted = `${yyyy}-${mm}-${dd}T${hh}:${min}`;
      console.log(`toInputFormat: ${dbValue} -> ${formatted}`);
      return formatted;
    } catch (error) {
      console.error("Error in toInputFormat:", error, dbValue);
      return "";
    }
  }

  function toTimeFormat(dbValue) {
    if (!dbValue) return "";
    const timePart = dbValue.includes(" ") ? dbValue.split(" ")[1] : dbValue;
    return timePart.slice(0, 5);
  }

  // Use the new utility function for datetime display

  const handleOpenDialog = async (editing = false, failure = null) => {
    if (editing && failure) {
      handleEdit(failure);
    } else {
      try {
        const { max_je_id } = await apiService.get("/job_errors/max-jeid");
        const nextJeId = (max_je_id || 0) + 1;

        setFormData({
          job_id: "",
          cl_id: clients.length > 0 ? clients[0].cl_id : "",
          env_id:
            environments.find((e) => e.name === "Production")?.env_id || "",
          app_id: applications.find((a) => a.name === "Boomi")?.app_id || "",
          client_job_id: "",
          je_id: String(nextJeId),
          received_date: new Date().toISOString().split("T")[0],
          reference_date_1: new Date().toISOString().split("T")[0],
          start_time: "",
          end_time: "",
          job_status: "",
          failure_message: "",
          comments: "",
          scheduled_time: "",
          last_run_time: "",
          success_cnt: "",
          failures_cnt: "",
          return_count: "",
          doc_id: "",
          run_time: "",
          err_identified_by: "",
          jobs_script: "",
          frequency: "",
          step_taken: "",
        });

        setIsEditing(false);
        setFormErrors({});
        setOpenDialog(true);
      } catch (err) {
        console.error("Error fetching je_id:", err);
        setErrorMessage("Error generating je_id. Try again later.");
      }
    }
  };

  const handleEdit = async (failure) => {
    const job = jobs.find((j) => j.job_id === failure.job_id);

    const formatForDateTimeLocal = (value) => {
      if (!value) return "";
      if (value.includes("T")) return value.slice(0, 16);
      return value.replace(" ", "T").slice(0, 16);
    };

    const startTime = formatForDateTimeLocal(failure.start_time);
    const endTime = formatForDateTimeLocal(failure.end_time);
    const runTime = calculateRunTime(startTime, endTime);

    setFormData({
      ...failure,
      start_time: startTime,
      end_time: endTime,
      received_date: failure.received_date
        ? toFailureTrackingInputFormat(failure.received_date)
        : "",
      reference_date_1: failure.reference_date_1
        ? toFailureTrackingInputFormat(failure.reference_date_1)
        : "",
      scheduled_time: failure.scheduled_time
        ? formatForDateTimeLocal(failure.scheduled_time)
        : "",
      last_run_time: failure.last_run_time
        ? toTimeFormat(failure.last_run_time)
        : "",
      je_id: failure.je_id ? String(failure.je_id) : "",
      client_job_id: failure.client_job_id ? String(failure.client_job_id) : "",
      job_id: failure.job_id ? String(failure.job_id) : "",
      cl_id: failure.cl_id || "",
      env_id: failure.env_id || "",
      app_id: failure.app_id || "",
      job_status: failure.job_status || "",
      failure_message: failure.failure_message || "",
      comments: failure.comments || "",
      success_cnt:
        failure.success_cnt !== null && failure.success_cnt !== undefined
          ? String(failure.success_cnt)
          : "",
      failures_cnt:
        failure.failures_cnt !== null && failure.failures_cnt !== undefined
          ? String(failure.failures_cnt)
          : "",
      return_count:
        failure.return_count !== null && failure.return_count !== undefined
          ? String(failure.return_count)
          : "",
      doc_id: failure.doc_id || "",
      run_time: runTime,
      err_identified_by: failure.err_identified_by || "",
      jobs_script: job?.jobs_script || "",
      frequency: job?.frequency || "",
      step_taken: failure.step_taken || "",
    });

    setIsEditing(true);
    setFormErrors({});
    setOpenDialog(true);
  };

  // Store original form data after formData is set during edit
  useEffect(() => {
    if (isEditing && openDialog && Object.keys(formData).length > 0) {
      setOriginalFormData({ ...formData });
    }
  }, [isEditing, openDialog]); // Only run when entering edit mode
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const startTime = formData.start_time
      ? toDbFormat(formData.start_time)
      : null;
    const endTime = formData.end_time ? toDbFormat(formData.end_time) : null;
    const lastRunTime = formData.last_run_time
      ? toDbFormat(formData.last_run_time, true)
      : null;

    if (startTime && endTime && new Date(endTime) < new Date(startTime)) {
      setErrorMessage("End time cannot be earlier than start time.");
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }

    const payload = {
      env_id: formData.env_id,
      app_id: formData.app_id,
      client_job_id: formData.client_job_id,
      job_id: formData.job_id,
      je_id: formData.je_id, // Add the missing je_id field
      received_date: formData.received_date,
      reference_date_1: formData.reference_date_1 || null,
      start_time: startTime,
      end_time: endTime,
      job_status: formData.job_status,
      failure_message: formData.failure_message || null,
      comments: formData.comments || null,
      scheduled_time: formData.scheduled_time
        ? toDbFormat(formData.scheduled_time)
        : null,
      last_run_time: lastRunTime,
      success_cnt:
        formData.success_cnt !== "" && !isNaN(formData.success_cnt)
          ? parseInt(formData.success_cnt)
          : null,
      failures_cnt:
        formData.failures_cnt !== "" && !isNaN(formData.failures_cnt)
          ? parseInt(formData.failures_cnt)
          : null,
      return_count:
        formData.return_count !== "" && !isNaN(formData.return_count)
          ? parseInt(formData.return_count)
          : null,
      doc_id: formData.doc_id || null,
      run_time: formData.run_time || null,
      err_identified_by: formData.err_identified_by || null,
    };

    try {
      if (isEditing) {
        await apiService.put(`/job_errors/${formData.je_id}`, payload);
        setSuccessMessage("Failure updated!");
      } else {
        await apiService.post(`/job_errors`, payload);
        setSuccessMessage("Failure recorded successfully!");
      }

      setFormData({
        job_id: "",
        cl_id: "",
        env_id: "",
        app_id: "",
        client_job_id: "",
        je_id: "",
        received_date: new Date().toISOString().split("T")[0],
        // reference_date_1: new Date().toISOString().split("T")[0],
        reference_date_1:
          filter.reference_date_1 || new Date().toISOString().split("T")[0],
        start_time: "",
        end_time: "",
        job_status: "",
        failure_message: "",
        comments: "",
        scheduled_time: "",
        last_run_time: "",
        success_cnt: "",
        failures_cnt: "",
        return_count: "",
        doc_id: "",
        run_time: "",
        err_identified_by: "",
        jobs_script: "",
        frequency: "",
        step_taken: "",
      });
      setIsEditing(false);
      setOpenDialog(false);
      fetchFailures();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Submission error:", error);
      const errorMsg = error.message || "An error occurred";
      setErrorMessage(`Failed to submit: ${errorMsg}`);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleDelete = (je_id) => {
    setSelectedJeId(je_id);
    setOpenDeleteDialog(true);
  };
  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setSelectedJeId(null);
  };

  const confirmDelete = async () => {
    if (!selectedJeId) return;

    try {
      await apiService.delete(`/job_errors/${selectedJeId}`);
      fetchFailures();
      setSuccessMessage("Job error deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage("Delete failed: " + error.message);
      setTimeout(() => setErrorMessage(""), 5000);
    }

    setOpenDeleteDialog(false);
    setSelectedJeId(null);
  };

  const handleCloneClick = (je_id) => {
    setConfirmClone({ open: true, je_id });
  };

  const performClone = async (je_id) => {
    try {
      await apiService.post(`/job_errors/clone/${je_id}`, {});

      setSuccessMessage("Job error and its details cloned successfully!");
      fetchFailures();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error cloning job error:", error);
      const errorMessage = error.message;
      setErrorMessage(`Failed to clone: ${errorMessage}`);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleToggleFlag = async (je_id, currentFlag) => {
    try {
      const newFlag = currentFlag === 1 ? 0 : 1;

      setFailures((prevFailures) =>
        prevFailures.map((f) =>
          f.je_id === je_id ? { ...f, active_flag: newFlag } : f,
        ),
      );

      const response = await apiService.patch(`/job_errors/toggle/${je_id}`, {
        active_flag: newFlag,
      });

      setSuccessMessage("Active flag updated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      setFailures((prevFailures) =>
        prevFailures.map((f) =>
          f.je_id === je_id ? { ...f, active_flag: currentFlag } : f,
        ),
      );
      setErrorMessage("Failed to toggle active flag. Please try again.");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleDetails = async (job) => {
    setSelectedJeId(job.je_id);
    setOpenDetailsDialog(true);
    setIsFetchingDetails(true);
    setSelectedJobScript(job.jobs_script);

    try {
      const data = await apiService.get(`/job_error_details/${job.je_id}`);
      setSelectedJobDetails(data);
    } catch (error) {
      console.error("Error fetching job details:", error);
      setSelectedJobDetails([]);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleRefreshDetails = async (jeId) => {
    const id = jeId || selectedJeId;
    if (!id) return;

    try {
      const data = await apiService.get(`/job_error_details/${id}`);
      setSelectedJobDetails(data);

      if (Array.isArray(data) && data.length > 0) {
        const times = [];

        data.forEach((row) => {
          if (row.error_time) {
            const parts = row.error_time
              .split(",")
              .map((t) => t.trim())
              .filter(Boolean);
            let baseDate = "";
            const first = parts[0];
            if (/\d{2}\/\d{2}\/\d{4}/.test(first)) {
              const [m, d, y] = first.split(" ")[0].split("/");
              baseDate = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
            }

            parts.forEach((p) => {
              let dateObj = null;
              if (/\d{2}\/\d{2}\/\d{4}/.test(p)) {
                dateObj = new Date(p);
              } else if (baseDate) {
                dateObj = new Date(`${baseDate} ${p}`);
              }
              if (dateObj && !isNaN(dateObj.getTime())) times.push(dateObj);
            });
          }
        });

        // Calculate totals
        const totalOccurrence = data.reduce(
          (sum, row) => sum + (parseInt(row.occurrence, 10) || 0),
          0,
        );

        // Calculate start/end times
        let start_time = null;
        let end_time = null;
        if (times.length > 0) {
          const minTime = new Date(Math.min(...times));
          const maxTime = new Date(Math.max(...times));
          const formatDb = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
              2,
              "0",
            )}-${String(d.getDate()).padStart(2, "0")} ${String(
              d.getHours(),
            ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:00`;
          start_time = formatDb(minTime);
          end_time = formatDb(maxTime);
        }

        // Update parent record
        await apiService.put(`/job_errors/${id}`, {
          start_time,
          end_time,
          failures_cnt: totalOccurrence,
        });

        // Update parent table immediately
        setFailures((prev) =>
          prev.map((f) =>
            f.je_id?.toString() === id.toString()
              ? { ...f, start_time, end_time, failures_cnt: totalOccurrence }
              : f,
          ),
        );
      }
    } catch (error) {
      console.error("Error refreshing job details:", error);
    }
  };

  const updateSingleField = async (je_id, field, value) => {
    try {
      const failure = failures.find((f) => f.je_id === je_id);
      if (!failure) throw new Error("Failure record not found");

      let formattedValue = value;
      let updatedRunTime = failure.run_time;

      if (
        field === "start_time" ||
        field === "end_time" ||
        field === "scheduled_time"
      ) {
        if (!value)
          throw new Error(`${field.replace("_", " ")} cannot be empty.`);
        formattedValue = toDbFormat(value);

        let newStartTime = failure.start_time;
        let newEndTime = failure.end_time;

        if (field === "start_time") {
          newStartTime = formattedValue;
          if (
            failure.end_time &&
            new Date(formattedValue) > new Date(failure.end_time)
          ) {
            throw new Error("Start time cannot be later than end time.");
          }
        } else if (field === "end_time") {
          newEndTime = formattedValue;
          if (
            failure.start_time &&
            new Date(formattedValue) < new Date(failure.start_time)
          ) {
            throw new Error("End time cannot be earlier than start time.");
          }
        }

        const startForCalc =
          field === "start_time" ? value : toInputFormat(failure.start_time);
        const endForCalc =
          field === "end_time" ? value : toInputFormat(failure.end_time);
        updatedRunTime = calculateRunTime(startForCalc, endForCalc);
      } else if (field === "last_run_time") {
        if (!value)
          throw new Error(`${field.replace("_", " ")} cannot be empty.`);
        formattedValue = toDbFormat(value, true);
      } else if (field === "received_date" || field === "reference_date_1") {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          throw new Error(`Invalid ${field.replace("_", " ")} format.`);
        }
        formattedValue = value;
      } else if (field === "failure_message" || field === "comments") {
        formattedValue = value || "";
      }

      // For partial updates, send only the changed field and required identifiers
      const payload = {
        je_id,
        [field]: formattedValue,
        ...(field === "start_time" || field === "end_time"
          ? { run_time: updatedRunTime }
          : {}),
        // Include mandatory fields to satisfy backend validation
        cl_id: failure.cl_id,
        env_id: failure.env_id,
        app_id: failure.app_id,
        job_id: failure.job_id,
        client_job_id: failure.client_job_id,
        received_date: failure.received_date,
        start_time: failure.start_time,
        end_time: failure.end_time,
        job_status: failure.job_status,
      };

      const response = await apiService.put(`/job_errors/${je_id}`, payload);

      setFailures((prevFailures) =>
        prevFailures.map((f) =>
          f.je_id === je_id
            ? { ...f, [field]: formattedValue, run_time: updatedRunTime }
            : f,
        ),
      );
      setEditError("");
      setEditSuccess(`${field.replace("_", " ")} updated successfully!`);
      setTimeout(() => {
        setEditSuccess("");
        setEditField(null);
      }, 3000);
    } catch (err) {
      console.error("Error updating field:", err);
      setEditError(err.message || "Failed to update field");
      setEditSuccess("");
      throw err;
    }
  };

  const totalFailures = failures.reduce(
    (sum, f) => sum + (parseInt(f.failures_cnt, 10) || 0),
    0,
  );

  const calculateRunTime = (startTime, endTime) => {
    if (!startTime || !endTime) return "00:00:00";

    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return "00:00:00";

      const diffMs = end - start;
      if (diffMs < 0) return "00:00:00";

      const diffSeconds = Math.floor(diffMs / 1000);
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      const seconds = diffSeconds % 60;

      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0",
      )}:${String(seconds).padStart(2, "0")}`;
    } catch (error) {
      console.error("Error calculating run_time:", error);
      return "00:00:00";
    }
  };

  useEffect(() => {
    const runTime = calculateRunTime(formData.start_time, formData.end_time);
    setFormData((prev) => ({
      ...prev,
      run_time: runTime,
    }));
  }, [formData.start_time, formData.end_time]);

  const columns = [
    "client_name",
    "env_name",
    "app_id",
    "je_id",
    "job_id",
    "client_job_id",
    "jobs_script",
    "frequency",
    // "scope",
    "process_type",
    "reference_date_1",
    "received_date",
    // "scheduled_time",
    // "last_run_time",
    // "success_cnt",
    "failures_cnt",
    // "return_count",
    // "doc_id",
    "failure_message",
    // "start_time",
    // "end_time",
    // "run_time",
    "job_status",
    // "err_identified_by",
    "comments",
    "active_flag",
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Failure Tracking Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Failure Tracking
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Filter Toggle */}
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                color: "#666",
                height: "35px",
                width: "30px",
                borderRadius: "8px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              {showFilters ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>

            {/* Add + */}
            <Button
              onClick={handleOpenDialog}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Failure Count Chip */}
            <Tooltip title="Overall Count" arrow>
              <Chip
                label={`Failure Count: ${totalFailures}`}
                sx={{
                  fontWeight: "bold",
                  fontSize: "11px",
                  bgcolor: "#f5f5f5",
                  color: "#666",
                  borderRadius: "8px",
                  height: "35px",
                  cursor: "default",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#e8f4fd",
                    color: "#1976d2",
                  },
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
            </Tooltip>

            {/* Export CSV */}
            <Tooltip title="Export CSV" arrow>
              <IconButton
                onClick={() => setOpenExportDialog(true)}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <FileDownloadIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Clear Filters */}
            <Tooltip title="Clear Filters" arrow>
              <IconButton
                onClick={handleResetFilter}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <CloseIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={handleOpen}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIconSVG style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (currentPage > 1) setCurrentPage(currentPage - 1);
              }}
              disabled={currentPage <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
                currentPage * rowsPerPage,
                totalRecords,
              )} of ${totalRecords}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (currentPage < totalPages) setCurrentPage(currentPage + 1);
              }}
              disabled={currentPage >= totalPages}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Filters Section */}
      {showFilters && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          flexWrap="wrap"
          gap={1.5}
          sx={{
            mt: 0.5,
            mb: 2,
            ml: -1,
            pl: 0,
          }}
        >
          {/* Settings Menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                p: 1,
                width: 200,
                maxHeight: 300,
                overflowY: "auto",
                borderRadius: 2,
              },
            }}
          >
            {Object.keys(defaultColumns).map((col) => (
              <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    border: "1px solid #ccc",
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#fff",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color: "#333",
                      fontWeight: "Bold",
                    }}
                  >
                    {columnLabels[col] || col}
                  </Typography>
                  <Switch
                    size="small"
                    checked={visibleColumns[col]}
                    onChange={(e) =>
                      setVisibleColumns((prev) => ({
                        ...prev,
                        [col]: e.target.checked,
                      }))
                    }
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#2DB0E4",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#2DB0E4",
                        },
                    }}
                  />
                </Box>
              </MenuItem>
            ))}
          </Menu>

          {/* Export CSV Dialog */}
          <Dialog
            open={openExportDialog}
            onClose={() => setOpenExportDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              sx={{
                fontWeight: "600",
                fontSize: "16px",
                py: 1.5,
              }}
            >
              Export Failure Tracking Data
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Helper Text */}

                {/* Reported Date Range */}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <TextField
                    label="Reported Date From"
                    type="date"
                    size="small"
                    value={exportFilters.reportedDateFrom}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        reportedDateFrom: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": { height: 40 },
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        padding: "10px 12px",
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    }}
                  />
                  <TextField
                    label="Reported Date To"
                    type="date"
                    size="small"
                    value={exportFilters.reportedDateTo}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        reportedDateTo: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": { height: 40 },
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        padding: "10px 12px",
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    }}
                  />
                </Box>

                {/* Received Date Range */}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <TextField
                    label="Received Date From"
                    type="date"
                    size="small"
                    value={exportFilters.receivedDateFrom}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        receivedDateFrom: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": { height: 40 },
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        padding: "10px 12px",
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    }}
                  />
                  <TextField
                    label="Received Date To"
                    type="date"
                    size="small"
                    value={exportFilters.receivedDateTo}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        receivedDateTo: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-root": { height: 40 },
                      "& .MuiInputBase-input": {
                        fontSize: "0.875rem",
                        padding: "10px 12px",
                      },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    }}
                  />
                </Box>

                {/* Environment Filter */}
                <FormControl size="small" fullWidth>
                  <InputLabel shrink sx={{ fontSize: "0.875rem" }}>
                    Environment
                  </InputLabel>
                  <Select
                    value={exportFilters.env_id}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        env_id: e.target.value,
                      }))
                    }
                    label="Environment"
                    displayEmpty
                    sx={{
                      "& .MuiInputBase-root": { height: 40 },
                      "& .MuiSelect-select": {
                        fontSize: "0.875rem",
                        padding: "10px 12px",
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
                      All Environments
                    </MenuItem>
                    {environments.map((env) => (
                      <MenuItem
                        key={env.env_id}
                        value={env.env_id}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {env.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Application Filter */}
                <FormControl size="small" fullWidth>
                  <InputLabel shrink sx={{ fontSize: "0.875rem" }}>
                    Application
                  </InputLabel>
                  <Select
                    value={exportFilters.app_id}
                    onChange={(e) =>
                      setExportFilters((prev) => ({
                        ...prev,
                        app_id: e.target.value,
                      }))
                    }
                    label="Application"
                    displayEmpty
                    sx={{
                      "& .MuiInputBase-root": { height: 40 },
                      "& .MuiSelect-select": {
                        fontSize: "0.875rem",
                        padding: "10px 12px",
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
                      All Applications
                    </MenuItem>
                    {applications.map((app) => (
                      <MenuItem
                        key={app.app_id}
                        value={app.app_id}
                        sx={{ fontSize: "0.875rem" }}
                      >
                        {app.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 1.5 }}>
              <Button
                onClick={() => setOpenExportDialog(false)}
                sx={{ fontSize: "0.875rem", textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleExportCSV}
                variant="contained"
                disabled={
                  !(
                    exportFilters.reportedDateFrom &&
                    exportFilters.reportedDateTo
                  ) &&
                  !(
                    exportFilters.receivedDateFrom &&
                    exportFilters.receivedDateTo
                  )
                }
                sx={{ fontSize: "0.875rem", textTransform: "none" }}
              >
                Export
              </Button>
            </DialogActions>
          </Dialog>

          {/* Environment Filter */}
          <Autocomplete
            options={uniqueEnvs}
            getOptionLabel={(option) => {
              const env = environments.find((e) => e.env_id === option);
              return env ? env.name : option;
            }}
            value={filter.env_id || null}
            onChange={(event, newValue) =>
              handleFilterChange({
                target: { name: "env_id", value: newValue || "" },
              })
            }
            ListboxProps={{
              sx: {
                "& .MuiAutocomplete-option": {
                  fontSize: "0.875rem",
                  minHeight: 32,
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Environment"
                size="small"
                sx={{
                  minWidth: 140,
                  "& .MuiInputBase-root": { height: 36 },
                  "& .MuiInputBase-input": {
                    padding: "8px 10px",
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                }}
                InputLabelProps={{ shrink: true }}
              />
            )}
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* Application Filter */}
          <Autocomplete
            options={
              filter.env_id && filteredApplications.length > 0
                ? filteredApplications.map((app) => app.app_id)
                : uniqueApps
            }
            getOptionLabel={(option) => {
              const app = (
                filter.env_id && filteredApplications.length > 0
                  ? filteredApplications
                  : applications
              ).find((a) => a.app_id === option);
              return app ? app.name : option;
            }}
            value={filter.app_id || null}
            onChange={(event, newValue) =>
              handleFilterChange({
                target: {
                  name: "app_id",
                  value: newValue || "",
                },
              })
            }
            ListboxProps={{
              sx: {
                "& .MuiAutocomplete-option": {
                  fontSize: "0.875rem",
                  minHeight: "32px",
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Application"
                size="small"
                sx={{
                  minWidth: 140,
                  "& .MuiInputBase-root": { height: 36 },
                  "& .MuiInputBase-input": {
                    padding: "8px 10px",
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                }}
                InputLabelProps={{ shrink: true }}
              />
            )}
            isOptionEqualToValue={(option, value) => option === value}
          />

          {/* Job ID Filter */}
          <Autocomplete
            size="small"
            options={
              filter.env_id && filter.app_id && filteredJobs.length > 0
                ? filteredJobs
                : jobsListForFilters
            }
            getOptionLabel={(option) => option.job_id || ""}
            inputValue={filter.job_id || ""}
            onInputChange={(_, newValue) => {
              setFilter((prev) => ({ ...prev, job_id: newValue || "" }));
            }}
            onChange={(_, newValue) => {
              setFilter((prev) => ({
                ...prev,
                job_id: newValue ? newValue.job_id : filter.job_id,
              }));
            }}
            value={null}
            freeSolo
            selectOnFocus
            clearOnBlur={false}
            clearOnEscape={false}
            handleHomeEndKeys
            popupIcon={<ExpandMoreIcon sx={{ color: "#666" }} />}
            sx={{
              "& .MuiAutocomplete-popupIndicator": {
                "&:hover": { backgroundColor: "transparent" },
              },
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const lower = inputValue.toLowerCase();
              return options.filter((opt) =>
                opt.job_id?.toLowerCase().includes(lower),
              );
            }}
            ListboxProps={{
              sx: {
                "& .MuiAutocomplete-option": {
                  fontSize: "0.875rem",
                  minHeight: "32px",
                },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Job ID"
                size="small"
                sx={{
                  minWidth: 100,
                  "& .MuiInputBase-root": { height: 36 },
                  "& .MuiInputBase-input": {
                    padding: "8px 10px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                }}
                InputLabelProps={{ shrink: true }}
              />
            )}
            noOptionsText="No matching Job ID"
            renderOption={(props, option) => (
              <li {...props} key={option.job_id}>
                {option.job_id}
              </li>
            )}
          />

          {/* Job Script Filter */}
          <Autocomplete
            size="small"
            options={
              filter.env_id && filter.app_id && filteredJobs.length > 0
                ? filteredJobs
                : jobsListForFilters
            }
            getOptionLabel={(option) => option.jobs_script || ""}
            inputValue={filter.jobs_script || ""}
            onInputChange={(_, newValue) => {
              setFilter((prev) => ({ ...prev, jobs_script: newValue || "" }));
            }}
            onChange={(_, newValue) => {
              setFilter((prev) => ({
                ...prev,
                jobs_script: newValue
                  ? newValue.jobs_script
                  : filter.jobs_script,
              }));
            }}
            value={null}
            freeSolo
            selectOnFocus
            clearOnBlur={false}
            clearOnEscape={false}
            handleHomeEndKeys
            popupIcon={<ExpandMoreIcon sx={{ color: "#666" }} />}
            sx={{
              "& .MuiAutocomplete-popupIndicator": {
                "&:hover": { backgroundColor: "transparent" },
              },
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              const lower = inputValue.toLowerCase();
              return options.filter((opt) =>
                opt.jobs_script?.toLowerCase().includes(lower),
              );
            }}
            ListboxProps={{
              sx: {
                "& .MuiAutocomplete-option": {
                  fontSize: "0.875rem",
                  minHeight: "32px",
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Job Script"
                size="small"
                sx={{
                  minWidth: 220,
                  "& .MuiInputBase-root": { height: 36 },
                  "& .MuiInputBase-input": {
                    padding: "8px 10px",
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                }}
                InputLabelProps={{ shrink: true }}
              />
            )}
            noOptionsText="No matching Job Script"
            renderOption={(props, option) => (
              <li {...props} key={option.job_id}>
                {option.jobs_script}
              </li>
            )}
          />

          {/* Folder Filter */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="folder-label" shrink sx={{ fontSize: "0.875rem" }}>
              Folder
            </InputLabel>

            <Select
              labelId="folder-label"
              name="folder"
              value={filter.folder}
              onChange={(e) =>
                setFilter((prev) => ({ ...prev, folder: e.target.value }))
              }
              label="Folder"
              displayEmpty
              renderValue={(value) => (value === "" ? "All" : value)}
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.875rem",
                },
                "& .MuiSelect-select": {
                  paddingY: "8px !important",
                  fontSize: "0.875rem",
                },
              }}
            >
              {/* Clear / All option */}
              <MenuItem value="" sx={{ fontSize: "0.875rem" }}>
                All
              </MenuItem>

              {uniqueFolders.map((folder) => (
                <MenuItem
                  key={folder}
                  value={folder}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {folder}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="status-label" shrink sx={{ fontSize: "0.875rem" }}>
              Status
            </InputLabel>
            <Select
              labelId="status-label"
              name="status"
              value={filter.status}
              displayEmpty
              renderValue={(value) => (value === "" ? "All" : value)}
              onChange={(e) =>
                setFilter((prev) => ({ ...prev, status: e.target.value }))
              }
              label="Status"
              sx={{
                "& .MuiInputBase-root": { height: 36 },
                "& .MuiInputBase-input": {
                  padding: "8px 10px",
                  fontSize: "0.875rem",
                },
                "& .MuiSelect-select": {
                  paddingY: "8px !important",
                  fontSize: "0.875rem",
                },
              }}
            >
              <MenuItem value="active" sx={{ fontSize: "0.875rem" }}>
                Active
              </MenuItem>
              <MenuItem value="inactive" sx={{ fontSize: "0.875rem" }}>
                Inactive
              </MenuItem>
              <MenuItem value="All" sx={{ fontSize: "0.875rem" }}>
                All
              </MenuItem>
            </Select>
          </FormControl>

          {/* From Reported Date */}
          <DatePicker
            selected={toDateObj(fromReportedDate)}
            onChange={(date) => handleFromDateChange(toISO(date))}
            dateFormat="MMM dd yyyy"
            isClearable
            withPortal
            customInput={
              <TextField
                label="From Reported Date"
                size="small"
                sx={{
                  // ─────────────── Main controls ───────────────
                  width: 140,
                  maxWidth: 160,

                  "& .MuiInputBase-root": {
                    height: 36,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  // Optional: make label behave better when narrow
                  "& .MuiInputLabel- shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />
            }
          />

          {/* To Reported Date */}
          <DatePicker
            selected={toDateObj(toReportedDate)}
            onChange={(date) => handleToDateChange(toISO(date))}
            dateFormat="MMM dd yyyy"
            isClearable
            withPortal
            customInput={
              <TextField
                label="To Reported Date"
                size="small"
                sx={{
                  // ─────────────── Main controls ───────────────
                  width: 140,
                  maxWidth: 160,

                  "& .MuiInputBase-root": {
                    height: 36,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  // Optional: make label behave better when narrow
                  "& .MuiInputLabel- shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
                InputLabelProps={{ shrink: true }}
              />
            }
          />

          {/* From Received Date */}
          <DatePicker
            selected={toDateObj(fromReceivedDate)}
            onChange={(date) => handleFromReceivedDateChange(toISO(date))}
            dateFormat="MMM dd yyyy"
            isClearable
            withPortal
            customInput={
              <TextField
                label="From Received Date"
                size="small"
                sx={{
                  // ─────────────── Main controls ───────────────
                  width: 140,
                  maxWidth: 160,

                  "& .MuiInputBase-root": {
                    height: 36,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  // Optional: make label behave better when narrow
                  "& .MuiInputLabel- shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
                InputLabelProps={{ shrink: true }}
                error={!!receivedDateError}
                helperText={receivedDateError}
              />
            }
          />

          {/* To Received Date */}
          <DatePicker
            selected={toDateObj(toReceivedDate)}
            onChange={(date) => handleToReceivedDateChange(toISO(date))}
            dateFormat="MMM dd yyyy"
            isClearable
            withPortal
            customInput={
              <TextField
                label="To Received Date"
                size="small"
                sx={{
                  // ─────────────── Main controls ───────────────
                  width: 140,

                  maxWidth: 160,

                  "& .MuiInputBase-root": {
                    height: 36,
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.875rem",
                  },
                  // Optional: make label behave better when narrow
                  "& .MuiInputLabel- shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
                InputLabelProps={{ shrink: true }}
                error={!!receivedDateError}
                helperText={receivedDateError}
              />
            }
          />
        </Box>
      )}

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mt: 2,
          position: "relative",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 250px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table stickyHeader>
                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col] || col}
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingFailures ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          columns.filter((col) => visibleColumns[col]).length +
                          1
                        }
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : failures.length > 0 ? (
                    failures.map((job) => (
                      <TableRow key={job.je_id} sx={{ height: "35px" }}>
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                            flexWrap="nowrap"
                            sx={{ minWidth: "max-content" }}
                          >
                            {/* <IconButton
                              size="small"
                              onClick={() => {
                                const formattedStartTime = toInputFormat(
                                  job.start_time,
                                );

                                setEditField({
                                  je_id: job.je_id,
                                  field: "start_time",
                                  value: formattedStartTime,
                                });
                              }}
                              title="Update Start Time"
                            >
                              <StartTimeIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const formattedEndTime = toInputFormat(
                                  job.end_time,
                                );

                                setEditField({
                                  je_id: job.je_id,
                                  field: "end_time",
                                  value: formattedEndTime,
                                });
                              }}
                              title="Update End Time"
                            >
                              <EndTimeIcon fontSize="small" />
                            </IconButton> */}
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditField({
                                  je_id: job.je_id,
                                  field: "failure_message",
                                  value: job.failure_message || "",
                                });
                              }}
                              title="Failure Reason"
                            >
                              <FailureReasonIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditField({
                                  je_id: job.je_id,
                                  field: "comments",
                                  value: job.comments || "",
                                });
                              }}
                              title="Notes"
                            >
                              <NotesIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleCloneClick(job.je_id)}
                              title="Clone row"
                            >
                              <CloneIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(job.je_id)}
                              title="Delete"
                              sx={{ color: "error.main" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(job)}
                              title="Edit"
                              sx={{ color: "#2DB0E4" }}
                            >
                              <EditIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(job.je_id, job.active_flag)
                              }
                             title="Toggle Active Flag"
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    job.active_flag === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  transition: "color 0.1s",
                                  width: 12,
                                  height: 15,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDetails(job)}
                              title="View Details"
                              sx={{ color: "#2db0e4" }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {visibleColumns.client_name && (
                          <TableCell>{job.client_name || ""}</TableCell>
                        )}
                        {visibleColumns.env_name && (
                          <TableCell>{job.env_name || ""}</TableCell>
                        )}
                        {visibleColumns.app_id && (
                          <TableCell>{job.app_id || ""}</TableCell>
                        )}
                        {visibleColumns.je_id && (
                          <TableCell>{job.je_id || ""}</TableCell>
                        )}
                        {visibleColumns.job_id && (
                          <TableCell>{job.job_id || ""}</TableCell>
                        )}
                        {visibleColumns.client_job_id && (
                          <TableCell>{job.client_job_id || ""}</TableCell>
                        )}
                        {visibleColumns.jobs_script && (
                          <TableCell>{job.jobs_script || ""}</TableCell>
                        )}
                        {visibleColumns.frequency && (
                          <TableCell>{job.reference_1 || ""}</TableCell>
                        )}
                        {/* {visibleColumns.scope && (
                          <TableCell>{job.scope || ""}</TableCell>
                        )} */}
                        {visibleColumns.process_type && (
                          <TableCell>{job.process_type || ""}</TableCell>
                        )}
                        {visibleColumns.reference_date_1 && (
                          <TableCell>
                            {formatDateTimezoneFreeForFailureTracking(
                              job.reference_date_1,
                            ) || ""}
                          </TableCell>
                        )}
                        {visibleColumns.received_date && (
                          <TableCell>
                            {formatDateTimezoneFreeForFailureTracking(
                              job.received_date,
                            ) || ""}
                          </TableCell>
                        )}
                        {/* {visibleColumns.scheduled_time && (
                          <TableCell>
                            {job.scheduled_time
                              ? formatFailureTrackingDateTime(
                                  job.scheduled_time,
                                )
                              : ""}
                          </TableCell>
                        )}
                        {visibleColumns.last_run_time && (
                          <TableCell>
                            {job.last_run_time
                              ? toTimeFormat(job.last_run_time)
                              : ""}
                          </TableCell>
                        )} */}
                        {/* {visibleColumns.success_cnt && (
                          <TableCell>{job.success_cnt ?? ""}</TableCell>
                        )} */}
                        {visibleColumns.failures_cnt && (
                          <TableCell>{job.failures_cnt ?? ""}</TableCell>
                        )}
                        {/* {visibleColumns.return_count && (
                          <TableCell>{job.return_count ?? ""}</TableCell>
                        )} */}
                        {/* {visibleColumns.doc_id && (
                          <TableCell>{job.doc_id || ""}</TableCell>
                        )} */}
                        {visibleColumns.failure_message && (
                          <TableCell>{job.failure_message || ""}</TableCell>
                        )}
                        {/* {visibleColumns.start_time && (
                          <TableCell>
                            {formatFailureTrackingDateTime(job.start_time) ||
                              ""}
                          </TableCell>
                        )}
                        {visibleColumns.end_time && (
                          <TableCell>
                            {formatFailureTrackingDateTime(job.end_time) || ""}
                          </TableCell>
                        )}
                        {visibleColumns.run_time && (
                          <TableCell>
                            {job.run_time ? `${job.run_time}` : ""}
                          </TableCell>
                        )} */}
                        {visibleColumns.job_status && (
                          <TableCell>{job.job_status || ""}</TableCell>
                        )}
                        {/* {visibleColumns.err_identified_by && (
                          <TableCell>{job.err_identified_by || ""}</TableCell>
                        )} */}
                        {visibleColumns.comments && (
                          <TableCell>{job.comments || ""}</TableCell>
                        )}
                        {visibleColumns.active_flag && (
                          <TableCell>
                            {job.active_flag === 1 ? "Active" : "Inactive"}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={
                          columns.filter((col) => visibleColumns[col]).length +
                          1
                        }
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No matching data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>
      </Box>

      <ThemeProvider theme={theme}>
        <Dialog
          open={openDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            setOpenDialog(false);
          }}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              fontSize: "16px",
              py: 1,
            }}
          >
            {isEditing ? "Edit Failure" : "Add Failure"}
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 1.1 }}>
            {loadingDropdowns ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 0.5,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {errorMessage && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                  </Alert>
                )}
                {(() => {
                  const selectedApp = applications.find(
                    (app) => app.app_id === formData.app_id,
                  );
                  const isBoomiApp = selectedApp?.name === "Boomi";
                  const filteredJobs = formData.app_id
                    ? jobs.filter((job) => job.app_id === formData.app_id)
                    : jobs;

                  return (
                    <Box
                      component="form"
                      onSubmit={handleSubmit}
                      sx={{
                        maxWidth: "1200px",
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(4, 1fr)",
                          gap: 1,
                        }}
                      >
                        <TextField
                          label="Client"
                          value={
                            clients.find((c) => c.cl_id === formData.cl_id)
                              ?.name ||
                            formData.cl_id ||
                            ""
                          }
                          InputProps={{
                            readOnly: true,
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              backgroundColor: "white",
                              cursor: "not-allowed",
                              color: "grey",
                            },
                          }}
                          required
                          fullWidth
                        />
                        <Autocomplete
                          options={environments.map((env) => ({
                            value: env.env_id,
                            label: env.name,
                          }))}
                          getOptionLabel={(option) => option.label}
                          value={
                            environments
                              .map((env) => ({
                                value: env.env_id,
                                label: env.name,
                              }))
                              .find(
                                (option) => option.value === formData.env_id,
                              ) || null
                          }
                          onChange={(event, newValue) => {
                            handleChange({
                              target: {
                                name: "env_id",
                                value: newValue ? newValue.value : "",
                              },
                            });
                          }}
                          PaperProps={{
                            sx: {
                              zIndex: 1301,
                              maxHeight: "200px",
                              overflowY: "auto",
                            },
                          }}
                          ListboxProps={{
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: "0.875rem",
                                minHeight: "32px",
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Environment"
                              variant="outlined"
                              size="small"
                              error={!!formErrors.env_id}
                              helperText={formErrors.env_id}
                              required
                              sx={{
                                height: "35px",
                                display: "flex",
                                justifyContent: "center",
                              }}
                            />
                          )}
                        />

                        <Autocomplete
                          options={applications.map((app) => ({
                            value: app.app_id,
                            label: app.name,
                          }))}
                          getOptionLabel={(option) => option.label}
                          value={
                            applications
                              .map((app) => ({
                                value: app.app_id,
                                label: app.name,
                              }))
                              .find(
                                (option) => option.value === formData.app_id,
                              ) || null
                          }
                          onChange={(event, newValue) => {
                            handleChange({
                              target: {
                                name: "app_id",
                                value: newValue ? newValue.value : "",
                              },
                            });
                          }}
                          PaperProps={{
                            sx: {
                              zIndex: 1301,
                              maxHeight: "200px",
                              overflowY: "auto",
                            },
                          }}
                          ListboxProps={{
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: "0.875rem",
                                minHeight: "32px",
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Application"
                              variant="outlined"
                              disabled={!formData.env_id}
                              error={!!formErrors.app_id}
                              helperText={formErrors.app_id}
                              required
                              sx={{
                                minWidth: 140,

                                "& .MuiInputBase-root": {
                                  height: 35,
                                },

                                "& .MuiFormHelperText-root": {
                                  minHeight: "16px", // prevent jumping
                                },
                              }}
                            />
                          )}
                        />

                        <Autocomplete
                          options={filteredJobs}
                          getOptionLabel={(option) =>
                            `${option.job_id} - ${option.jobs_script}`
                          }
                          value={
                            filteredJobs.find(
                              (job) => job.job_id === formData.job_id,
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            handleAutocompleteChange(
                              "job_id",
                              newValue ? newValue.job_id : "",
                            );
                          }}
                          PaperProps={{
                            sx: {
                              zIndex: 1301,
                              maxHeight: "200px",
                              overflowY: "auto",
                            },
                          }}
                          ListboxProps={{
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: "0.875rem",
                                minHeight: "32px",
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Job ID"
                              variant="outlined"
                              error={!!formErrors.job_id}
                              helperText={formErrors.job_id}
                              required
                              sx={{
                                minWidth: 140,

                                "& .MuiInputBase-root": {
                                  height: 35,
                                },

                                "& .MuiFormHelperText-root": {
                                  minHeight: "16px", // prevent jumping
                                },
                              }}
                            />
                          )}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(4, 1fr)",
                          gap: 1,
                          mt: -1,
                        }}
                      >
                        {/* ------------- ROW 1 (5 fields) ------------- */}

                        <Autocomplete
                          options={filteredJobs}
                          getOptionLabel={(option) => `${option.client_job_id}`}
                          value={
                            filteredJobs.find(
                              (job) =>
                                job.client_job_id === formData.client_job_id,
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            handleAutocompleteChange(
                              "client_job_id",
                              newValue ? newValue.client_job_id : "",
                            );
                          }}
                          PaperProps={{
                            sx: {
                              zIndex: 1301,
                              maxHeight: "200px",
                              overflowY: "auto",
                            },
                          }}
                          ListboxProps={{
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: "0.875rem",
                                minHeight: "32px",
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Client Job ID"
                              variant="outlined"
                              error={!!formErrors.client_job_id}
                              helperText={formErrors.client_job_id}
                              sx={{
                                height: "35px",
                                display: "flex",
                                justifyContent: "center",
                              }}
                              InputProps={{
                                ...params.InputProps,
                                readOnly: !isEditing,
                              }}
                            />
                          )}
                        />
                        <TextField
                          label="Reported Date"
                          type="date"
                          name="reference_date_1"
                          value={formData.reference_date_1}
                          onChange={handleChange}
                          required
                          InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                          type="date"
                          label="Received Date"
                          name="received_date"
                          value={formData.received_date.substring(0, 10)}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                          required
                          error={!!formErrors.received_date}
                          helperText={formErrors.received_date}
                        />
                        <TextField
                          type="datetime-local"
                          label="Start Time"
                          name="start_time"
                          InputLabelProps={{ shrink: true }}
                          value={formData.start_time}
                          disabled
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              start_time: e.target.value,
                            });
                            setErrorMessage("");
                          }}
                          error={!!formErrors.start_time}
                          helperText={formErrors.start_time}
                        />
                        <TextField
                          type="datetime-local"
                          label="End Time"
                          name="end_time"
                          InputLabelProps={{ shrink: true }}
                          disabled
                          value={formData.end_time}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              end_time: e.target.value,
                            });
                            setErrorMessage("");
                          }}
                          error={!!formErrors.end_time}
                          helperText={formErrors.end_time}
                        />
                        <TextField
                          type="datetime-local"
                          label="Scheduled Time"
                          name="scheduled_time"
                          InputLabelProps={{ shrink: true }}
                          value={formData.scheduled_time}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              scheduled_time: e.target.value,
                            })
                          }
                          disabled={isBoomiApp}
                        />
                        {/* ------------- ROW 2 (5 fields) ------------- */}
                        <TextField
                          type="time"
                          label="Last Run Time"
                          name="last_run_time"
                          InputLabelProps={{ shrink: true }}
                          value={formData.last_run_time}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              last_run_time: e.target.value,
                            })
                          }
                          disabled={isBoomiApp}
                        />
                        <TextField
                          label="Success Count"
                          name="success_cnt"
                          value={formData.success_cnt}
                          onChange={handleChange}
                          type="number"
                          error={!!formErrors.success_cnt}
                          helperText={formErrors.success_cnt}
                          disabled={isBoomiApp}
                        />
                        <TextField
                          label="Failures Count"
                          name="failures_cnt"
                          value={formData.failures_cnt}
                          onChange={handleChange}
                          type="number"
                          error={!!formErrors.failures_cnt}
                          helperText={formErrors.failures_cnt}
                        />
                        <TextField
                          label="Return Count"
                          name="return_count"
                          value={formData.return_count}
                          onChange={handleChange}
                          type="number"
                          error={!!formErrors.return_count}
                          helperText={formErrors.return_count}
                          disabled={isBoomiApp}
                        />
                        {/* STATUS FIELD */}
                        <Autocomplete
                          options={[
                            { value: "FAILED", label: "FAILED" },
                            { value: "SUCCESS", label: "SUCCESS" },
                          ]}
                          getOptionLabel={(option) => option.label}
                          value={
                            [
                              { value: "FAILED", label: "FAILED" },
                              { value: "SUCCESS", label: "SUCCESS" },
                            ].find(
                              (option) => option.value === formData.job_status,
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            handleChange({
                              target: {
                                name: "job_status",
                                value: newValue ? newValue.value : "",
                              },
                            });
                          }}
                          ListboxProps={{
                            sx: {
                              "& .MuiAutocomplete-option": {
                                fontSize: "0.875rem",
                                minHeight: "32px",
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Status"
                              required
                              error={!!formErrors.job_status}
                              helperText={formErrors.job_status}
                            />
                          )}
                        />
                        {/* ------------- ROW 3 (5 fields) ------------- */}
                        {/* <TextField
                          label="Document ID"
                          name="doc_id"
                          value={formData.doc_id}
                          onChange={handleChange}
                          disabled={isBoomiApp}
                        /> */}
                        <TextField
                          label="Error Identified By"
                          name="err_identified_by"
                          value={formData.err_identified_by}
                          onChange={handleChange}
                          disabled={isBoomiApp}
                        />
                        {/* Fillers so row stays 5 columns */}
                        
                        <TextField
                          label="Failure Message"
                          name="failure_message"
                          value={formData.failure_message}
                          onChange={handleChange}
                          multiline
                          rows={4}
                          sx={{
                            gridColumn: "span 2",
                            ...compactMultilineStyles,
                            width: "100%",
                          }}
                        />
                        <TextField
                          label="Comments"
                          name="comments"
                          value={formData.comments}
                          onChange={handleChange}
                          multiline
                           rows={4}
                          sx={{
                            gridColumn: "span 2",
                            ...compactMultilineStyles,
                            width: "100%",
                          }}
                        />
                        <Box />
                        
                        <Box />
                      </Box>
                      <Snackbar
                        open={alert.open}
                        autoHideDuration={6000}
                        onClose={() => setAlert({ ...alert, open: false })}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }}
                      >
                        <Alert
                          onClose={() => setAlert({ ...alert, open: false })}
                          severity="success"
                          sx={{ width: "100%" }}
                        >
                          {alert.message}
                        </Alert>
                      </Snackbar>
                    </Box>
                  );
                })()}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || (isEditing && !hasFormChanged())}
            >
              {isEditing ? "Update" : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!editField}
          onClose={() => setEditField(null)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              fontWeight: "700",
              fontSize: "18px",
              py: 1,
            }}
          >
            Edit {editField?.field}
          </DialogTitle>
          <DialogContent dividers>
            {editField?.field === "start_time" ||
            editField?.field === "end_time" ||
            editField?.field === "scheduled_time" ? (
              <TextField
                autoFocus
                margin="dense"
                label={`New ${editField?.field.replace("_", " ")}`}
                type="datetime-local"
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={editField?.value || ""}
                onChange={(e) =>
                  setEditField({
                    ...editField,
                    value: e.target.value,
                  })
                }
              />
            ) : editField?.field === "reference_date_1" ||
              editField?.field === "received_date" ? (
              <TextField
                autoFocus
                margin="dense"
                label={`New ${editField?.field.replace("_", " ")}`}
                type="date"
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={editField?.value || ""}
                onChange={(e) =>
                  setEditField({
                    ...editField,
                    value: e.target.value,
                  })
                }
              />
            ) : (
              <TextField
                autoFocus
                margin="dense"
                label={`New ${editField?.field.replace("_", " ")}`}
                type="text"
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                value={editField?.value || ""}
                onChange={(e) =>
                  setEditField({
                    ...editField,
                    value: e.target.value,
                  })
                }
              />
            )}
            {editError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {editError}
              </Alert>
            )}
            {editSuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {editSuccess}
              </Alert>
            )}
          </DialogContent>
          <DialogActions
            sx={{ justifyContent: "flex-end", padding: "8px 24px" }}
          >
            <Button onClick={() => setEditField(null)} variant="text">
              CANCEL
            </Button>
            <Button
              onClick={async () => {
                setLoading(true);
                setEditError("");
                setEditSuccess("");
                try {
                  await updateSingleField(
                    editField.je_id,
                    editField.field,
                    editField.value,
                  );
                } catch (err) {
                } finally {
                  setLoading(false);
                  setTimeout(() => setEditField(null), 2000);
                }
              }}
              variant="contained"
              disabled={loading}
            >
              Update
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openDeleteDialog}
          onClose={(event, reason) => {
            if (reason === "backdropClick") return;
            cancelDelete();
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Delete</DialogTitle>

          <DialogContent>
            <Typography fontSize="0.9rem">
              {(() => {
                const selectedFailure = failures.find(
                  (item) => item.je_id === selectedJeId,
                );

                return (
                  <>
                    Are you sure you want to delete Job{" "}
                    <strong>
                      {selectedFailure?.job_id} - {selectedFailure?.jobs_script}
                    </strong>
                    ?
                  </>
                );
              })()}
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button onClick={cancelDelete}>Cancel</Button>

            <Button variant="contained" onClick={confirmDelete}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* Clone Confirmation Dialog */}
        <Dialog
          open={confirmClone.open}
          onClose={() => setConfirmClone({ open: false, je_id: null })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Clone</DialogTitle>

          <DialogContent>
            <Typography fontSize="0.9rem">
              Are you sure you want to clone this job error detail?
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setConfirmClone({ open: false, je_id: null })}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={() => {
                if (confirmClone.je_id) {
                  performClone(confirmClone.je_id);
                }
                setConfirmClone({ open: false, je_id: null });
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <FailureTrackingPopup
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          jobDetails={selectedJobDetails}
          je_id={selectedJeId}
          jobScript={selectedJobScript}
          jobError={failures.find((f) => f.je_id === selectedJeId)}
          onRefresh={() => handleRefreshDetails(selectedJeId)}
          selectedFolder={filter.folder}
          isFetchingDetails={isFetchingDetails}
          onOccurrenceSum={(sum) =>
            setFormData((prev) => ({ ...prev, failures_cnt: String(sum) }))
          }
        />
      </ThemeProvider>
    </Container>
  );
}

export default FailureTracking;
